import { Injectable, OnDestroy, signal, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { AccountingSelectors } from '../../../../../store/accounting/accounting.selectors';
import { Subscription } from 'rxjs';
import { AccountingProviderCompany } from '../../../../../_interfaces/accounting.interface';
import { toSignal } from '@angular/core/rxjs-interop';
import { AccountingApiService } from '../../../../../_services/accounting-service/accounting-api/accounting-api.service';
import { ToastService } from '../../../../../_services/toast-service/toast.service';
import {
  ClearAccountingState,
  LoadTaxRates,
  LoadTrackingCategories,
} from '../../../../../store/accounting/accounting.actions';

@Injectable({
  providedIn: 'root',
})
export class AccountingService implements OnDestroy {
  readonly accountingApiService = inject(AccountingApiService);
  readonly toastService = inject(ToastService);
  readonly store = inject(Store);

  readonly connectionStatus$ = this.store.select(AccountingSelectors.connectionStatus);
  readonly provider$ = this.store.select(AccountingSelectors.provider);
  readonly providerNewBankAccountsCreated$ = this.store.select(AccountingSelectors.providerNewBankAccountsCreated);
  readonly tenantDetail$ = this.store.select(AccountingSelectors.tenantDetail);
  readonly sourceAccount$ = this.store.select(AccountingSelectors.sourceAccount);
  readonly trackingCategories$ = this.store.select(AccountingSelectors.trackingCategories);
  readonly taxRates$ = this.store.select(AccountingSelectors.taxRates);

  readonly connectionStatus = toSignal(this.connectionStatus$, { initialValue: undefined });
  readonly provider = toSignal(this.provider$, { initialValue: undefined });
  readonly providerNewBankAccountsCreated = toSignal(this.providerNewBankAccountsCreated$, { initialValue: undefined });
  readonly tenantDetail = toSignal(this.tenantDetail$, { initialValue: undefined });
  readonly sourceAccount = toSignal(this.sourceAccount$, { initialValue: undefined });
  readonly trackingCategories = toSignal(this.trackingCategories$, { initialValue: [] });
  readonly taxRates = toSignal(this.taxRates$, { initialValue: [] });

  readonly busyConnectingQB = signal(false);
  readonly busyConnectingXERO = signal(false);
  readonly subscriptions = new Subscription();

  reconnectConnection(provider: AccountingProviderCompany) {
    this.accountingApiService.deleteAccountConnection().subscribe({
      next: () => {
        this.onConnect(provider);
      },
      error: () => {
        this.toastService.show('Something went wrong. The connection could not be disconnected.');
      },
    });
  }

  onConnect(provider: AccountingProviderCompany) {
    this.accountingApiService.requestAccountingConnectionUrl({ provider: provider }).subscribe({
      next: (res) => {
        this.store.dispatch(new ClearAccountingState());
        globalThis.location.href = res.url;
        this.busyConnectingQB.set(provider === AccountingProviderCompany.QUICKBOOKS);
        this.busyConnectingXERO.set(provider === AccountingProviderCompany.XERO);
      },
      error: (err) => {
        this.toastService.show(err.error.non_field_errors[0]);
      },
    });
  }

  /**
   * Load tracking categories (with caching)
   * Call this when entering a page that needs tracking categories
   */
  loadTrackingCategories() {
    this.store.dispatch(new LoadTrackingCategories());
  }

  /**
   * Load tax rates (with caching)
   * Call this when entering a page that needs tax rates
   */
  loadTaxRates() {
    this.store.dispatch(new LoadTaxRates());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
